import React from "react";

interface MetaPolicyStruct {
  [key: string]: any;
}

interface Props {
  struct: MetaPolicyStruct;
}

const PaidOnTheSpotSection: React.FC<Props> = ({ struct }) => {
  const notIncluded = {
    add_fee: struct.add_fee?.filter((i: any) => i.inclusion === "not_included") || [],
    check_in_check_out: struct.check_in_check_out?.filter((i: any) => i.inclusion === "not_included") || [],
    children: struct.children || [],
    extra_bed: struct.extra_bed?.filter((i: any) => i.inclusion === "not_included") || [],
    cot: struct.cot?.filter((i: any) => i.inclusion === "not_included") || [],
    internet: struct.internet?.filter((i: any) => i.inclusion === "not_included") || [],
    meal: struct.meal?.filter((i: any) => i.inclusion === "not_included") || [],
    deposit: struct.deposit || [],
    children_meal: struct.children_meal?.filter((i: any) => i.inclusion === "not_included") || [],
    parking: struct.parking?.filter((i: any) => i.inclusion === "not_included") || [],
    shuttle: struct.shuttle?.filter((i: any) => i.inclusion === "not_included") || [],
    pets: struct.pets?.filter((i: any) => i.inclusion === "not_included") || [],
  };

  const hasAny =
    Object.values(notIncluded).reduce((acc, arr) => acc + arr.length, 0) > 0;

  if (!hasAny) return null;

  return (
    <div className="mt-12 mx-4 md:mx-0 bg-white shadow-md rounded-lg border border-gray-200">
      <div className="bg-gray-100 p-4 rounded-t-lg">
        <h2 className="text-lg font-semibold text-gray-900">
          Paid on the spot
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 text-sm text-gray-800">
        {/* Add Fee */}
        {struct.add_fee?.length > 0 && (
          <div>
            <SectionHeader icon="money" title="Additional Services or Fees" />
            {struct.add_fee.map((fee: any, i: number) => (
              <div key={i} className="mb-1">
                {fee.fee_type.charAt(0).toUpperCase() + fee.fee_type.slice(1)}:{" "}
                {fee.price} {fee.currency} {fee.price_unit.replace(/_/g, " ")}
              </div>
            ))}
          </div>
        )}

        {/* Check-in Check-out */}
        {struct.check_in_check_out?.filter(
          (item: any) => item.inclusion === "not_included"
        ).length > 0 && (
          <div>
            <SectionHeader icon="checkin" title="Check-in/Check-out" />
            {struct.check_in_check_out
              .filter((item: any) => item.inclusion === "not_included")
              .map((item: any, i: number) => (
                <div key={i} className="mb-1">
                  {item.check_in_check_out_type.charAt(0).toUpperCase() +
                    item.check_in_check_out_type.slice(1)}
                  : {item.price} {item.currency}
                </div>
              ))}
          </div>
        )}

        {/* Children */}
        {struct.children?.length > 0 && (
          <div>
            <SectionHeader icon="kids" title="Children" />
            {struct.children.map((child: any, i: number) => (
              <div key={i} className="mb-1">
                <span className="font-medium">
                  Age {child.age_start} to {child.age_end}:
                </span>{" "}
                {child.price === "0" || child.price === ""
                  ? "Needs to be clarified on the spot"
                  : `${child.price} ${child.currency} Extra bed ${child.extra_bed}`}
              </div>
            ))}
          </div>
        )}

        {/* Additional Bed */}
        {struct.extra_bed?.filter(
          (bed: any) => bed.inclusion === "not_included"
        ).length > 0 && (
          <div>
            <SectionHeader icon="bed" title="Additional bed" />
            {struct.extra_bed
              .filter((bed: any) => bed.inclusion === "not_included")
              .map((bed: any, i: number) => (
                <div key={i}>
                  Price: {bed.price}{" "}
                  {bed.currency || "To be clarified on the spot"}{" "}
                  {bed.price_unit.replace(/_/g, " ")}
                </div>
              ))}
          </div>
        )}

        {/* Baby Cot */}
        {struct.cot?.filter((cot: any) => cot.inclusion === "not_included")
          .length > 0 && (
          <div>
            <SectionHeader icon="baby-bed" title="Baby Cot" />
            {struct.cot
              .filter((cot: any) => cot.inclusion === "not_included")
              .map((cot: any, i: number) => (
                <div key={i}>
                  {cot.price === "0" ? (
                    "To be clarified on the spot"
                  ) : (
                    <>
                      Price: {cot.price} {cot.currency}{" "}
                      {cot.price_unit.replace(/_/g, " ")}
                    </>
                  )}
                </div>
              ))}
          </div>
        )}

        {/* Internet */}
        {struct.internet?.filter((net: any) => net.inclusion === "not_included")
          .length > 0 && (
          <div>
            <SectionHeader icon="internet" title="Internet" />
            {struct.internet
              .filter((net: any) => net.inclusion === "not_included")
              .map((net: any, i: number) => (
                <div key={i}>
                  Location: {net.work_area} <br />
                  Price: {net.price} {net.currency}{" "}
                  {net.price_unit && `${net.price_unit.replace(/_/g, " ")}`}
                </div>
              ))}
          </div>
        )}

        {/* Meals */}
        {struct.meal?.filter((meal: any) => meal.inclusion === "not_included")
          .length > 0 && (
          <div>
            <SectionHeader icon="meals" title="Meals" />
            {struct.meal
              .filter((meal: any) => meal.inclusion === "not_included")
              .map((meal: any, i: number) => (
                <div key={i}>
                  {meal.meal_type.charAt(0).toUpperCase() +
                    meal.meal_type.slice(1)}
                  : {meal.price} {meal.currency}
                </div>
              ))}
          </div>
        )}

        {/* Deposit */}
        {struct.deposit?.length > 0 && (
          <div>
            <SectionHeader icon="deposit" title="Deposit" />
            {struct.deposit.map((dep: any, i: number) => (
              <div key={i}>
                {dep.deposit_type === "unspecified" ? "Deposit" : dep.deposit_type}:{" "}
                {dep.price} {dep.currency} {dep.price_unit.replace(/_/g, " ")}
              </div>
            ))}
          </div>
        )}

        {/* Meals for Children */}
        {struct.children_meal?.filter(
          (cm: any) => cm.inclusion === "not_included"
        ).length > 0 && (
          <div>
            <SectionHeader icon="meals" title="Meals for children" />
            {struct.children_meal
              .filter((cm: any) => cm.inclusion === "not_included")
              .map((cm: any, i: number) => (
                <div key={i}>
                  Breakfast (Age {cm.age_start}–{cm.age_end}): {cm.price}{" "}
                  {cm.currency}
                </div>
              ))}
          </div>
        )}

        {/* Parking */}
        {struct.parking?.filter((p: any) => p.inclusion === "not_included")
          .length > 0 && (
          <div>
            <SectionHeader icon="parking" title="Parking" />
            {struct.parking
              .filter((p: any) => p.inclusion === "not_included")
              .map((p: any, i: number) => (
                <div key={i}>
                  Price: {p.price} {p.currency}{" "}
                  {p.price_unit.replace(/_/g, " ")}
                </div>
              ))}
          </div>
        )}

        {/* Transfer */}
        {struct.shuttle?.filter((s: any) => s.inclusion === "not_included")
          .length > 0 && (
          <div>
            <SectionHeader icon="transfer" title="Transfer" />
            {struct.shuttle
              .filter((s: any) => s.inclusion === "not_included")
              .map((s: any, i: number) => (
                <div key={i}>
                  {s.destination_type.charAt(0).toUpperCase() +
                    s.destination_type.slice(1)}
                  : {s.price} {s.currency}
                </div>
              ))}
          </div>
        )}

        {/* Pets */}
        {struct.pets?.filter((p: any) => p.inclusion === "not_included")
          .length > 0 && (
          <div>
            <SectionHeader icon="pets" title="Pets" />
            {struct.pets
              .filter((p: any) => p.inclusion === "not_included")
              .map((p: any, i: number) => (
                <div key={i}>
                  Pets are allowed – {p.price} {p.currency}{" "}
                  {p.price_unit.replace(/_/g, " ")}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

const SectionHeader = ({ icon, title }: { icon: string; title: string }) => (
  <div className="flex items-center gap-2 text-md font-semibold text-gray-800 mb-1">
    <div className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded-md overflow-hidden">
      <img
        src={`/icons/${icon}.svg`}
        alt={`${icon} icon`}
        className="w-4 h-4 object-contain"
        onError={(e) => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = "/icons/default.svg";
        }}
      />
    </div>
    <div>{title}</div>
  </div>
);

export default PaidOnTheSpotSection;
